import { Icon } from '@iconify/react';
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import config from '../config';
import { useAuth } from '../context/AuthContext';

const MyVenuesLayer = () => {
    const { auth } = useAuth();
    const [venues, setVenues] = useState([]);
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [formData, setFormData] = useState({
        business_name: '',
        description: '',
        license_number: '',
        phone: '',
        email: '',
        address: '',
        latitude: '',
        longitude: ''
    });

    const fetchData = useCallback(async () => {
        try {
            const [venuesRes, subRes] = await Promise.all([
                auth.fetch(`${config.API_BASE_URL}/api/venues/my-venues`),
                auth.fetch(`${config.API_BASE_URL}/api/payments/subscription`)
            ]);

            if (venuesRes.ok) {
                const venuesData = await venuesRes.json();
                setVenues(venuesData);
            }

            if (subRes.ok) {
                const subData = await subRes.json();
                setUsage(subData.usage);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [auth]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const formDataToSend = new FormData();
            Object.keys(formData).forEach(key => {
                formDataToSend.append(key, formData[key]);
            });
            if (imageFile) {
                formDataToSend.append('image', imageFile);
            }

            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues`, {
                method: 'POST',
                body: formDataToSend
            });
            
            const data = await response.json();

            if (response.ok) {
                toast.success('Venue created successfully');
                setShowAddModal(false);
                setFormData({
                    business_name: '',
                    description: '',
                    license_number: '',
                    phone: '',
                    email: '',
                    address: '',
                    latitude: '',
                    longitude: ''
                });
                setImageFile(null);
                setImagePreview(null);
                fetchData(); // Refresh list and usage
            } else {
                if (response.status === 403 && data.error === 'Subscription Limit Exceeded') {
                    toast.error(
                        <div>
                            <p>{data.message}</p>
                            <button 
                                className="btn btn-sm btn-light mt-2" 
                                onClick={() => {
                                    const tab = document.querySelector('[title="My Plan"]');
                                    if(tab) tab.click();
                                }}
                            >
                                Upgrade Plan
                            </button>
                        </div>,
                        { autoClose: false }
                    );
                } else {
                    toast.error(data.error || 'Failed to create venue');
                }
            }
        } catch (error) {
            console.error('Error creating venue:', error);
            toast.error('An error occurred');
        }
    };

    if (loading) return <div>Loading...</div>;

    const usagePercent = usage ? (usage.venues / usage.max_venues) * 100 : 0;
    const isLimitReached = usage ? usage.venues >= usage.max_venues : false;

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div className="d-flex align-items-center gap-3">
                    <h6 className="text-lg fw-semibold mb-0">My Venues</h6>
                    {usage && (
                        <div className="d-flex align-items-center gap-2 bg-neutral-50 px-12 py-6 radius-8 border">
                            <span className="text-sm text-secondary-light">Usage:</span>
                            <span className={`text-sm fw-medium ${isLimitReached ? 'text-danger-main' : 'text-primary-600'}`}>
                                {usage.venues} / {usage.max_venues}
                            </span>
                            <div className="w-80-px h-4-px bg-neutral-200 radius-4 overflow-hidden">
                                <div 
                                    className={`h-100 radius-4 ${isLimitReached ? 'bg-danger-main' : 'bg-primary-600'}`} 
                                    style={{ width: `${Math.min(usagePercent, 100)}%` }}
                                ></div>
                            </div>
                        </div>
                    )}
                </div>
                <button 
                    className={`btn radius-8 px-20 py-11 d-flex align-items-center gap-2 ${isLimitReached ? 'btn-secondary-100 text-secondary-400 cursor-not-allowed' : 'btn-primary-600'}`}
                    onClick={() => !isLimitReached && setShowAddModal(true)}
                    disabled={isLimitReached}
                    title={isLimitReached ? "Upgrade plan to add more venues" : "Add Venue"}
                >
                    <Icon icon="mdi:plus" className="text-xl" />
                    Add Venue
                </button>
            </div>
            <div className="card-body p-24">
                {venues.length === 0 ? (
                    <div className="text-center py-5">
                        <Icon icon="mdi:store-off" className="text-6xl text-secondary-light mb-3" />
                        <p className="text-secondary-light">No venues found. Add your first venue to get started.</p>
                    </div>
                ) : (
                    <div className="row gy-4">
                        {venues.map((venue) => (
                            <div key={venue.id} className="col-xxl-4 col-md-6">
                                <div className="card h-100 border shadow-sm radius-12">
                                    <div className="card-body p-20">
                                        <div className="d-flex align-items-center justify-content-between mb-16">
                                            <h6 className="text-md fw-semibold mb-0">{venue.business_name}</h6>
                                            <span className={`badge radius-8 px-12 py-6 text-sm ${
                                                venue.verification_status === 'approved' ? 'bg-success-focus text-success-main' :
                                                venue.verification_status === 'pending' ? 'bg-warning-focus text-warning-main' :
                                                'bg-danger-focus text-danger-main'
                                            }`}>
                                                {venue.verification_status}
                                            </span>
                                        </div>
                                        <div className="d-flex flex-column gap-2">
                                            <div className="d-flex align-items-center gap-2 text-secondary-light">
                                                <Icon icon="mdi:map-marker" className="text-lg" />
                                                <span className="text-sm line-clamp-1">{venue.address || 'No address'}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2 text-secondary-light">
                                                <Icon icon="mdi:phone" className="text-lg" />
                                                <span className="text-sm">{venue.phone || 'No phone'}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2 text-secondary-light">
                                                <Icon icon="mdi:email" className="text-lg" />
                                                <span className="text-sm">{venue.email || 'No email'}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-transparent border-top py-12 px-20 d-flex justify-content-end gap-2">
                                        <button className="btn btn-sm btn-outline-primary-600 radius-8">
                                            Manage
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add Venue Modal */}
            {showAddModal && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content radius-16">
                            <div className="modal-header">
                                <h5 className="modal-title">Add New Venue</h5>
                                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="row gy-3">
                                        <div className="col-12 text-center mb-3">
                                            <div className="position-relative d-inline-block">
                                                <div className="w-80-px h-80-px rounded-circle border d-flex justify-content-center align-items-center overflow-hidden bg-neutral-50">
                                                    {imagePreview ? (
                                                        <img src={imagePreview} alt="Preview" className="w-100 h-100 object-fit-cover" />
                                                    ) : (
                                                        <Icon icon="mdi:camera-outline" className="text-3xl text-secondary-light" />
                                                    )}
                                                </div>
                                                <label htmlFor="venueImage" className="position-absolute bottom-0 end-0 bg-primary-600 text-white w-24-px h-24-px rounded-circle d-flex justify-content-center align-items-center cursor-pointer hover-scale">
                                                    <Icon icon="mdi:plus" className="text-sm" />
                                                </label>
                                                <input 
                                                    type="file" 
                                                    id="venueImage" 
                                                    className="d-none" 
                                                    accept="image/*"
                                                    onChange={handleFileChange}
                                                />
                                            </div>
                                            <p className="text-xs text-secondary-light mt-2">Upload Venue Logo</p>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">Business Name</label>
                                            <input type="text" className="form-control radius-8" name="business_name" value={formData.business_name} onChange={handleInputChange} required />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">License Number</label>
                                            <input type="text" className="form-control radius-8" name="license_number" value={formData.license_number} onChange={handleInputChange} />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">Phone</label>
                                            <input type="text" className="form-control radius-8" name="phone" value={formData.phone} onChange={handleInputChange} />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">Email</label>
                                            <input type="email" className="form-control radius-8" name="email" value={formData.email} onChange={handleInputChange} />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">Address</label>
                                            <textarea className="form-control radius-8" name="address" value={formData.address} onChange={handleInputChange} rows="2"></textarea>
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">Description</label>
                                            <textarea className="form-control radius-8" name="description" value={formData.description} onChange={handleInputChange} rows="3"></textarea>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-end gap-3 mt-24">
                                        <button type="button" className="btn btn-secondary-100 text-secondary-light radius-8 px-20 py-11" onClick={() => setShowAddModal(false)}>Cancel</button>
                                        <button type="submit" className="btn btn-primary-600 radius-8 px-20 py-11">Create Venue</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyVenuesLayer;
